import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import { upsertSeasonExhibitors } from '../../ReduxToolkit/Reducers/fairManagement';

const EMPTY = { name: '', website: '', type: 'brand', email: '', paid: false, re_booked: false };

// One exhibitor into a season, through the same upload path as the CSV (same checks, duplicate
// skip, rebooked rule and profile linking)
const AddExhibitorModal = ({ open, organizationId, season, defaultType, onClose, onAdded }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...EMPTY, type: defaultType || 'brand' });
  }, [open, defaultType]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    const row = {
      name: form.name.trim(),
      website: form.website.trim(),
      type: form.type,
      paid: form.paid,
      re_booked: form.re_booked,
      ...(form.email.trim() && { email: form.email.trim() }),
    };
    const result = await dispatch(upsertSeasonExhibitors(organizationId, season.id, [row]));
    setSaving(false);
    if (!result) return;

    const { summary, invalid_records } = result.data;
    if (summary.added) {
      toast.success(`${row.name} added to ${season.season_name}`, { autoClose: 3000 });
      onAdded?.();
      onClose();
    } else if (invalid_records.length) {
      toast.error(invalid_records[0].reason, { autoClose: 3000 });
    } else {
      toast.warning('This website is already in this season', { autoClose: 3000 });
    }
  };

  return (
    <Modal isOpen={open} toggle={saving ? undefined : onClose} centered>
      <ModalHeader toggle={saving ? undefined : onClose}>
        Add exhibitor to {season?.season_name} {season?.year}
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>Customer name</Label>
          <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>Website</Label>
          <Input
            value={form.website}
            onChange={(e) => set('website', e.target.value)}
            placeholder="e.g. acme.com"
          />
        </FormGroup>
        <FormGroup>
          <Label>Type</Label>
          <Input type="select" value={form.type} onChange={(e) => set('type', e.target.value)}>
            <option value="brand">Brand</option>
            <option value="retailer">Retailer</option>
          </Input>
        </FormGroup>
        <FormGroup>
          <Label>Email (optional)</Label>
          <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
        </FormGroup>
        <FormGroup check>
          <Input
            type="checkbox"
            id="add-exhibitor-paid"
            checked={form.paid}
            onChange={(e) => set('paid', e.target.checked)}
          />
          <Label check for="add-exhibitor-paid">
            Registered
          </Label>
        </FormGroup>
        <FormGroup check>
          <Input
            type="checkbox"
            id="add-exhibitor-rebooked"
            checked={form.re_booked}
            onChange={(e) => set('re_booked', e.target.checked)}
          />
          <Label check for="add-exhibitor-rebooked">
            Rebooked customer
          </Label>
          {/* The upload rule still marks it rebooked when its website is in an earlier season */}
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" outline onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSave}
          disabled={saving || !form.name.trim() || !form.website.trim()}
        >
          {saving ? <Spinner size="sm" color="light" /> : 'Add'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddExhibitorModal;
